import { useQuery } from '@apollo/client';
import type { CommunityGroup } from '@communities/community-groups/domain';
import { GET_COMMUNITY_GROUP } from '@communities/community-groups/graphql';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { useMemo } from 'react';

import { useCurrentCommunityGroupId } from './useCurrentCommunityGroupId';

interface CommunityGroupResponse {
  communityGroup: CommunityGroup;
}

export function useCommunityGroupDetail() {
  const communityGroupId = useCurrentCommunityGroupId();

  const { data, loading, error, refetch } = useQuery<CommunityGroupResponse>(GET_COMMUNITY_GROUP, {
    variables: { id: communityGroupId },
    skip: !communityGroupId,
    fetchPolicy: 'cache-first',
  });

  const communityGroup = data?.communityGroup || null;
  const isCommunityGroupAdmin = useMemo(() => communityGroup?.roles?.includes('Admin') ?? false, [communityGroup?.roles]);

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  return {
    data: communityGroup,
    isCommunityGroupAdmin,
    loading,
    errors,
    refetch,
  };
}

export default useCommunityGroupDetail;
